import { Box, Button, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import type { RootState } from "../../../../../store/Index";
import { useSelector } from "react-redux";
import { MemoryBook } from "../../../../../types/memory-table";
import { User } from "../../../../users-admin/types/User";
import { toast } from "react-toastify";
import { showErrorToast } from "../../../../../components/common/ShowToast";
import PropTypes from "prop-types";

interface Props {
  memoryBooks: MemoryBook[];
  isLoading: boolean;
  error: boolean;
  uploadUser: User | null;
  isAdmin: boolean;
}

export const CurrentMemoryBookInfo: React.FC<Props> = ({
  memoryBooks,
  isLoading,
  error,
  uploadUser: _uploadUser,
  isAdmin,
}) => {
  void _uploadUser;
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const determineUploadedName = (): string | undefined => {
    const currentBook = memoryBooks[0];
    if (!currentBook.created_by) {
      return undefined;
    }
    if (
      !currentBook.created_by.first_name ||
      !currentBook.created_by.last_name
    ) {
      return currentBook.created_by.username;
    }
    return `${currentBook.created_by.first_name} ${currentBook.created_by.last_name}`;
  };

  const downloadCurrentMemoryBook = () => {
    const url = memoryBooks[0].file;
    if (!url) {
      showErrorToast("No file has been uploaded for this memory book yet.");
      return;
    }
    try {
      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `memory_book_${memoryBooks[0].id}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(a.href);
        })
        .catch((err) => {
          console.error(err);
          showErrorToast("Failed to download memory book. Please try again.");
        });
    } catch (err) {
      console.error(err);
      toast.error("Failed to download memory book. Please try again", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const viewBook = () => {
    const url = memoryBooks[0].file;
    if (!url) {
      showErrorToast("No file has been uploaded for this memory book yet.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box padding={1.5} bgcolor={colors.primary[800]} borderRadius={1} mb={2}>
      <Typography variant="body1" mb={1} fontWeight={500}>
        Current File
      </Typography>

      {!isLoading && error && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          py={2}
        >
          <DescriptionIcon sx={{ color: colors.primary[300], mb: 1 }} />
          <Typography variant="body2">
            Cannot display current file at the moment
          </Typography>
        </Box>
      )}

      {!isLoading && !error && memoryBooks.length === 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          py={2}
        >
          <DescriptionIcon sx={{ color: colors.primary[300], mb: 1 }} />
          <Typography variant="body2">
            No memory book available at the moment
          </Typography>
        </Box>
      )}

      {!isLoading && !error && memoryBooks.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1.5,
          }}
        >
          {/* File info */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <DescriptionIcon
              sx={{ color: colors.primary[300], flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {memoryBooks[0].file
                  ? memoryBooks[0].file.split("/").at(-1)
                  : "No file uploaded yet"}
              </Typography>
              {isAdmin && (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {determineUploadedName()}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button
              variant="contained"
              size="small"
              onClick={downloadCurrentMemoryBook}
            >
              <DownloadIcon fontSize="small" sx={{ mr: 0.5 }} />
              Download
            </Button>
            <Button size="small" onClick={viewBook}>
              <FullscreenIcon fontSize="small" sx={{ mr: 0.5 }} />
              View Book
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

CurrentMemoryBookInfo.propTypes = {
  memoryBooks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      file: PropTypes.string,
      created_by: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        username: PropTypes.string,
      }).isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  uploadUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isAdmin: PropTypes.bool.isRequired,
};
